import { useCallback, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import { buildAddressFromSearchResult } from '../utils';

interface UseTreatmentReportAddressSearchParams {
  watch: UseFormWatch<TreatmentReportFormData>;
  setValue: UseFormSetValue<TreatmentReportFormData>;
}

export const useTreatmentReportAddressSearch = ({
  watch,
  setValue,
}: UseTreatmentReportAddressSearchParams) => {
  const addressRef = useRef<InputRef | null>(null);
  const [isAddressSearchModalOpen, setIsAddressSearchModalOpen] = useState(false);

  const { search: handleAddressSearch, isPending: isAddressSearching } = useAddressLookup({
    getValues: () => ({
      pc1: watch('postal_code1') || '',
      pc2: watch('postal_code2') || '',
      address: watch('address') || '',
    }),
    applyResult: ({ pc1, pc2, address }) => {
      setValue('postal_code1', pc1, { shouldDirty: true });
      setValue('postal_code2', pc2, { shouldDirty: true });
      setValue('address', address, { shouldDirty: true });
    },
    openModal: () => setIsAddressSearchModalOpen(true),
    onAfterApply: () => {
      const nativeInput = addressRef.current?.input;
      if (nativeInput) {
        nativeInput.focus();
        nativeInput.setSelectionRange(nativeInput.value.length, nativeInput.value.length);
      }
    },
  });

  const handleAddressSearchSelect = useCallback(
    (result: any) => {
      setValue('postal_code1', result.postal_code1 || '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('postal_code2', result.postal_code2 || '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('address', buildAddressFromSearchResult(result), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setIsAddressSearchModalOpen(false);
    },
    [setValue]
  );

  return {
    addressRef,
    handleAddressSearch,
    handleAddressSearchSelect,
    isAddressSearching,
    isAddressSearchModalOpen,
    setIsAddressSearchModalOpen,
  };
};

